import dayjs from 'dayjs';

import type { IndividualPaymentParcelSaveViewModel } from '@/shared/api/generated';

import type { ParcelEditFormValues } from '../hooks/useParcelEditFormSchema';
import type { ParcelFieldName } from '../types/parcelEdit';

/** Argumentos de `buildParcelEditPayload`. */
export interface ParcelEditPayloadInput {
  readonly parcelId: number;
  readonly values: ParcelEditFormValues;
  /**
   * Campos que a fonte de semeadura não cobre — omitidos do corpo.
   *
   * Vem de `useParcelEditSeed`, para que a lista tenha uma origem só.
   */
  readonly unseededFields: readonly ParcelFieldName[];
}

/**
 * Monta o corpo do `PUT` de edição de parcela.
 *
 * Três decisões estão materializadas aqui:
 *
 * 1. **Campos não semeados são omitidos.** O formulário não conhece o valor atual
 *    deles, então enviá-los vazios apagaria o que existe no registro — o endpoint
 *    substitui a parcela. Com a semeadura pelo detalhe (`GET /parcels/{parcelId}`)
 *    a lista chega vazia e todos os campos viajam; a guarda continua aqui porque é
 *    ela que torna seguro semear de uma fonte parcial, se alguma tela precisar.
 * 2. **`multiple` e `amountOfParcel` nunca são enviados.** Recorrência é da criação:
 *    o `PUT` edita uma parcela, não gera cópias.
 * 3. **As datas saem como data-hora ISO.** O formulário guarda `YYYY-MM-DD` (o
 *    formato do `DatePicker`) e o schema gerado pede `date-time`, então a conversão
 *    fica nesta fronteira — como em `buildParcelCreatePayload`.
 *
 * As duas flags de reset viajam apenas quando ligadas: `false` explícito e ausência
 * significam a mesma coisa para o backend, e o corpo carrega só o que o operador
 * pediu.
 *
 * @param input - Id da parcela, valores validados e campos sem semeadura.
 * @returns Corpo do `PUT`, pronto para o hook gerado.
 *
 * @example
 * ```ts
 * buildParcelEditPayload({ parcelId: 42, values, unseededFields: ['fine'] });
 * // { id: 42, typeId: 3, ..., sem `fine` }
 * ```
 */
export function buildParcelEditPayload({
  parcelId,
  values,
  unseededFields,
}: ParcelEditPayloadInput): IndividualPaymentParcelSaveViewModel {
  const isSeeded = (field: ParcelFieldName) => !unseededFields.includes(field);

  const optionalNumber = (
    key: keyof IndividualPaymentParcelSaveViewModel,
    field: ParcelFieldName,
    value: number | null | undefined,
  ) => (!isSeeded(field) || value === null || value === undefined ? {} : { [key]: value });

  const optionalText = (
    key: keyof IndividualPaymentParcelSaveViewModel,
    field: ParcelFieldName,
    value: string | null | undefined,
  ) =>
    !isSeeded(field) || value === null || value === undefined || value.trim() === ''
      ? {}
      : { [key]: value.trim() };

  const optionalDate = (
    key: keyof IndividualPaymentParcelSaveViewModel,
    field: ParcelFieldName,
    value: string | null | undefined,
  ) => {
    if (!isSeeded(field) || value === null || value === undefined || value === '') return {};
    const parsed = dayjs(value);
    return parsed.isValid() ? { [key]: parsed.toISOString() } : {};
  };

  // O schema gerado passa por `ToZod`, e o Zod infere alguns campos de forma opaca;
  // a forma real é a do `IndividualPaymentParcelSaveViewModel`.
  const text = (value: unknown): string | null | undefined =>
    typeof value === 'string' ? value : null;
  const numeric = (value: unknown): number | null | undefined =>
    typeof value === 'number' ? value : null;
  const flag = (value: unknown): boolean => value === true;

  return {
    id: parcelId,
    typeId: values.typeId,
    numberParcel: values.numberParcel,
    total: values.total,
    paymentMethod: values.paymentMethod,
    ...optionalDate('expirationDate', 'expirationDate', values.expirationDate),
    ...optionalNumber('serial', 'serial', numeric(values.serial)),
    ...optionalNumber(
      'discountPunctualPercentage',
      'discountPunctualPercentage',
      values.discountPunctualPercentage,
    ),
    ...optionalNumber('retroactiveDiscount', 'retroactiveDiscount', values.retroactiveDiscount),
    ...optionalNumber('totalDiscount', 'totalDiscount', values.totalDiscount),
    ...optionalNumber('fine', 'fine', values.fine),
    ...optionalNumber('fractionOfParcel', 'fractionOfParcel', numeric(values.fractionOfParcel)),
    ...optionalText('message', 'message', text(values.message)),
    ...optionalText('observation', 'observation', text(values.observation)),
    ...optionalText('invoiceNumber', 'invoiceNumber', text(values.invoiceNumber)),
    ...optionalDate('invoiceIssueDate', 'invoiceIssueDate', text(values.invoiceIssueDate)),
    ...(flag(values.removePunctualDiscount) ? { removePunctualDiscount: true } : {}),
    ...(flag(values.resetExtendedExpiration) ? { resetExtendedExpiration: true } : {}),
  };
}
